/**
 * Service-area lookup — pure functions, fully testable.
 *
 * Decision precedence:
 *   1. Suburb name (case/space insensitive) — primary
 *   2. Postcode override (Glasshouse / Gympie postcodes hard-excluded even
 *      if a name lookup didn't find them)
 *   3. Postcode range (Sunshine Coast + Noosa) → "in"
 *   4. Unknown
 *
 * Returns one of: "in" | "excluded" | "drop_off" | "unknown"
 */
import data from "@/data/service_area.json";

const norm = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

const suburbIndex = new Map(data.suburbs.map((s) => [norm(s.name), s]));

const excludedPostcodes = new Set([
  ...(data.regions.excluded_glasshouse?.postcodes_override || []),
  ...(data.regions.excluded_gympie?.postcodes_override || []),
]);

const inRanges = data.regions.in?.postcode_ranges || [];

function postcodeInRange(pc) {
  const n = parseInt(pc, 10);
  if (!Number.isFinite(n)) return false;
  return inRanges.some(([lo, hi]) => n >= lo && n <= hi);
}

/**
 * Look up a suburb / postcode and return {status, suburb, postcode, matched_by}.
 * `suburb` is the canonical-cased name from the JSON when matched, or whatever
 * the caller passed in.
 */
export function checkServiceArea({ suburb, postcode } = {}) {
  const subKey = norm(suburb);
  const pc = (postcode || "").toString().trim();

  // 1. Direct suburb match
  if (subKey && suburbIndex.has(subKey)) {
    const hit = suburbIndex.get(subKey);
    return {
      status: hit.status,
      suburb: hit.name,
      postcode: hit.postcode,
      matched_by: "suburb",
    };
  }

  // 2. Explicit postcode-level exclusion (Glasshouse / Gympie buckets)
  if (pc && excludedPostcodes.has(pc)) {
    return {
      status: "excluded",
      suburb: suburb || "",
      postcode: pc,
      matched_by: "postcode_excluded",
    };
  }

  // 3. Postcode range — Sunshine Coast + Noosa
  if (pc && postcodeInRange(pc)) {
    return {
      status: "in",
      suburb: suburb || "",
      postcode: pc,
      matched_by: "postcode_range",
    };
  }

  // 4. Drop-off / contact suburbs that didn't hit by name (rare)
  // 5. Anything else
  return {
    status: "unknown",
    suburb: suburb || "",
    postcode: pc,
    matched_by: "none",
  };
}

/** Friendly verdict copy keyed by status. `{suburb}` is interpolated.
 *
 * Aware Feb-2026 — the "Do we service your area?" widget and its
 * associated legacy out-of-area URL variant were retired when Aware
 * switched to a national posted-kit service. The pure-function
 * lookups below are kept only for the `parseAddress` helper still
 * used by the booking flow's internal partner-allocation logic.
 */
export const VERDICT_COPY = {
  in: {
    tone: "green",
    headline: "We service {suburb}. Book your water test online with one flat all-inclusive price.",
    cta: "Book a Test",
    cta_to: "/book-water-test",
  },
  drop_off: {
    tone: "amber",
    headline: "{suburb} is outside our mobile service area, but you can drop a sample at our Noosaville lab.",
    cta: "Sample Drop-off Details",
    cta_to: "/book-water-test",
  },
  excluded: {
    tone: "red",
    headline: "We don\u2019t currently service {suburb}. Contact us to discuss sample drop-off or courier options.",
    cta: "Contact Us",
    cta_to: "/contact",
  },
  unknown: {
    tone: "slate",
    headline: "We couldn\u2019t confirm that suburb. Contact us and we\u2019ll check it for you.",
    cta: "Contact Us",
    cta_to: "/contact",
  },
};

export function interpolateVerdict(status, suburb) {
  const v = VERDICT_COPY[status] || VERDICT_COPY.unknown;
  const display = suburb && suburb.trim() ? suburb : "your area";
  return { ...v, headline: v.headline.replace("{suburb}", display) };
}

export const LAB_ADDRESS = data.lab_address;
export const SERVICE_AREA_VERSION = data.version;

/**
 * Heuristically pull suburb + postcode from a free-form Australian address.
 * Shared with the internal partner-allocation lookup on Booking.jsx.
 */
export function parseAddress(raw) {
  const text = (raw || "").trim();
  if (!text) return { suburb: "", postcode: "" };
  const pcMatch = text.match(/\b(\d{4})\b/);
  const postcode = pcMatch ? pcMatch[1] : "";
  let rest = text.replace(/\b\d{4}\b/g, "").replace(/\b(qld|queensland)\b/gi, "").trim();
  if (rest.includes(",")) {
    rest = rest.split(",").map((p) => p.trim()).filter(Boolean).pop() || rest;
  }
  rest = rest
    .replace(/^\d+\s+/, "")
    .replace(/\b(rd|st|street|road|drive|dr|ave|avenue|lane|ln|court|ct|crescent|cr|parade|pde|way|terrace|tce|highway|hwy)\b/gi, "")
    .trim();
  return { suburb: rest, postcode };
}

/**
 * Split a free-form AU address (usually returned by Google Places /
 * typed into `<AddressAutocomplete />`) into the 5 fields the backend
 * needs to build a Starshipit / Australia Post shipment.
 *
 * Handles the common Google-shaped formats:
 *   "12 Beach Rd, Noosaville QLD 4566, Australia"
 *   "12 Beach Rd, Noosaville QLD 4566"
 *   "Unit 4, 12 Beach Rd, Noosaville, QLD 4566"
 *
 * Returns `{ line1, line2, suburb, state, postcode, country }` with
 * best-effort extraction. Any field that can't be inferred stays as
 * an empty string so the caller can decide whether to prompt.
 * NEVER throws — safe to call with garbage input.
 */
export function parseAusAddress(raw) {
  const out = { line1: "", line2: "", suburb: "", state: "", postcode: "", country: "Australia" };
  const text = (raw || "").trim();
  if (!text) return out;

  // Split on commas, drop empties.
  const parts = text.split(",").map((p) => p.trim()).filter(Boolean);

  // Peel country off the tail if it says AU / Australia (case-insensitive).
  if (parts.length > 1 && /^(au|australia)$/i.test(parts[parts.length - 1])) {
    out.country = "Australia";
    parts.pop();
  }

  // Peel the "SUBURB STATE POSTCODE" chunk off the tail.
  // Matches patterns like  "Noosaville QLD 4566"  or  "Noosaville, QLD, 4566".
  const stateRe = /\b(NSW|VIC|QLD|SA|WA|TAS|ACT|NT)\b/i;
  const hadMultipleParts = parts.length > 1;
  const tail = parts.length ? parts[parts.length - 1] : "";
  const tailPc = tail.match(/\b(\d{4})\b/);
  const tailState = tail.match(stateRe);
  if (tailPc || tailState) {
    // The last chunk is the suburb/state/postcode row.
    out.postcode = tailPc ? tailPc[1] : "";
    out.state    = tailState ? tailState[1].toUpperCase() : "";
    // Everything else in that chunk is the suburb — UNLESS this was a
    // single comma-less blob (customer typed a free-text address
    // instead of picking an autocomplete suggestion, e.g.
    // "174 Hohenhaus Rd Moogerah 4309"). In that case the "everything
    // else" is street + suburb smushed together; dumping it all into
    // `suburb` used to leave `line1` empty, which fails backend address
    // validation for every mail-out booking shaped like this (Nov 2026,
    // SCWT-34E6D6). Peel the LAST word off as the suburb instead and
    // feed the rest back in as the street line.
    let remainder = tail;
    if (tailPc)    remainder = remainder.replace(tailPc[0], "");
    if (tailState) remainder = remainder.replace(tailState[0], "");
    remainder = remainder.replace(/\s+/g, " ").trim();
    parts.pop();
    if (!hadMultipleParts && parts.length === 0) {
      const words = remainder.split(/\s+/).filter(Boolean);
      if (words.length > 1) {
        // Nov 2026 — a single trailing word is usually right ("...
        // Moogerah 4309" → suburb "Moogerah"), but Australia has many
        // common MULTI-word localities ("Gold Coast", "Sunshine
        // Coast", "Mount Isa", "Port Douglas", "North Sydney", ...).
        // Grabbing only the last word would mis-split those (e.g.
        // suburb "Coast" / line1 "...Gold"). Walk backwards from the
        // last word and keep folding in another word while the token
        // just before the current suburb start is a recognised
        // locality prefix — a bounded, best-effort improvement, not a
        // full gazetteer lookup.
        const localityPrefix = /^(mount|mt|port|north|south|east|west|upper|lower|new|golden|sunshine|gold|surfers|coffs|alice|point|cape|lake|glass|broad)$/i;
        let splitAt = words.length - 1;
        while (splitAt > 0 && localityPrefix.test(words[splitAt - 1])) splitAt -= 1;
        out.suburb = words.slice(splitAt).join(" ");
        parts.push(words.slice(0, splitAt).join(" "));
      } else {
        out.suburb = remainder;
      }
    } else {
      out.suburb = remainder;
    }
  } else if (parts.length >= 2) {
    // No state/postcode detected in the last chunk — fall back to
    // treating it as the suburb.
    out.suburb = parts.pop();
  }

  // The remainder (possibly 1 or 2 chunks) is the street address.
  if (parts.length >= 2) {
    out.line1 = parts[0];
    out.line2 = parts.slice(1).join(", ");
  } else if (parts.length === 1) {
    out.line1 = parts[0];
  }
  return out;
}

export default data;
